/**
 * Loads `FamilyChangeRequest` rows for the admin "Family requests" tab
 * (#127 slice 5). Fully paginates `client.models.FamilyChangeRequest.list`
 * (never trusts a single page — the same class of bug fixed in
 * MemberApplications.tsx's `loadApplications` and AdminDashboard's
 * `loadDashboardStats` for Member.list), sorts newest first by `createdAt`.
 *
 * Admin components use `generateClient({ authMode: 'userPool' })`. The
 * generated client type may not know about `FamilyChangeRequest` yet (the
 * backend is being built in parallel against the same contract), so this
 * casts through `any` the same way `manageFamilyClient.ts` (#127 slice 4)
 * does for not-yet-generated Lambda mutations.
 */
import { generateClient } from 'aws-amplify/data';
import type { FamilyChangeRequest, FamilyChangeRequestStatus } from './types';

const client = generateClient({ authMode: 'userPool' });

/**
 * The data client returns `a.json()` fields (person, householdBefore,
 * householdAfter) as JSON strings, sometimes double-encoded. Parse them into
 * objects; anything unparseable or the wrong shape becomes null so the detail
 * view can never crash on it (found on dev 10 Oct 2026).
 */
function parseJsonField(value: unknown): unknown {
  let v = value;
  for (let i = 0; i < 2 && typeof v === 'string'; i++) {
    try { v = JSON.parse(v); } catch { return null; }
  }
  return v && typeof v === 'object' ? v : null;
}

function toHousehold(value: unknown): { adults: number; juniors: number } | null {
  const v = parseJsonField(value) as { adults?: unknown; juniors?: unknown } | null;
  const ok = (n: unknown) => typeof n === 'number' && Number.isInteger(n) && n >= 0;
  return v && ok(v.adults) && ok(v.juniors) ? { adults: v.adults as number, juniors: v.juniors as number } : null;
}

export function normaliseFamilyChangeRequest(raw: any): FamilyChangeRequest {
  return {
    ...raw,
    person: parseJsonField(raw?.person) as FamilyChangeRequest['person'],
    householdBefore: toHousehold(raw?.householdBefore),
    householdAfter: toHousehold(raw?.householdAfter),
  };
}

/**
 * Lists every FamilyChangeRequest, optionally filtered by status, newest
 * first. Pass `status: 'ALL'` (or omit) for every status.
 */
export async function listFamilyChangeRequests(
  status?: FamilyChangeRequestStatus | 'ALL'
): Promise<FamilyChangeRequest[]> {
  const filter =
    status && status !== 'ALL' ? { status: { eq: status } } : undefined;
  const all: FamilyChangeRequest[] = [];
  let nextToken: string | null | undefined;
  do {
    const page: any = await (client.models as any).FamilyChangeRequest.list({
      filter,
      nextToken: nextToken || undefined,
    });
    all.push(...(page.data || []).map(normaliseFamilyChangeRequest));
    nextToken = page.nextToken;
  } while (nextToken);

  return all.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

/** The count of PENDING requests, for the tab's badge. Fully paginated. */
export async function countPendingFamilyChangeRequests(): Promise<number> {
  let count = 0;
  let nextToken: string | null | undefined;
  do {
    const page: any = await (client.models as any).FamilyChangeRequest.list({
      filter: { status: { eq: 'PENDING' } },
      nextToken: nextToken || undefined,
    });
    count += (page.data || []).length;
    nextToken = page.nextToken;
  } while (nextToken);
  return count;
}

/** Looks up Member rows by id (for showing names/numbers on a request). Best-effort; missing ids are skipped. */
export async function getMembersByIds(
  ids: string[]
): Promise<Map<string, { id: string; memberNumber?: string | null; firstName: string; lastName: string }>> {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  const map = new Map<
    string,
    { id: string; memberNumber?: string | null; firstName: string; lastName: string }
  >();
  await Promise.all(
    unique.map(async (id) => {
      try {
        const result: any = await client.models.Member.get({ id });
        if (result?.data) {
          map.set(id, {
            id: result.data.id,
            memberNumber: result.data.memberNumber,
            firstName: result.data.firstName,
            lastName: result.data.lastName,
          });
        }
      } catch {
        /* best-effort; missing member just shows the id */
      }
    })
  );
  return map;
}
